import { HomeIcon } from 'src/components/Icons/HomeIcon'
import { RadioCheckedIcon } from 'src/components/Icons/RadioCheckedIcon'
import { EditIcon } from 'src/components/Icons/EditIcon'
import { DefaultIcon } from 'src/components/Icons/DefaultIcon'
import { DeleteIcon } from 'src/components/Icons/DeleteIcon'
import classNames from 'classnames'
import type { OrderForm } from 'src/sdk/checkout/entity/orderForm'
import type { AddressData } from 'src/sdk/checkout/data/types/orderformAPI'
import { maskCEP } from 'src/utils/formatCEP'

import { resolveAddressReferenceLabel } from '../../utils/addressReferenceLabel'
import { usePostalCodeAddressCardActions } from '../../Hooks/usePostalCodeAddressCardActions'
import type { LocalAddressesPayload } from '../../domain/addressManagerReconciliation'

interface PostalCodeAddressCardProps {
  addressData?: AddressData
  label?: string
  isDefault?: boolean
  isSelected?: boolean
  checked?: boolean
  orderFormId: string
  syncOrderFormFn: (orderForm: OrderForm) => void
  onClick?: () => void
  showActionsBar?: boolean
  onEdit?: () => void
  onSelect?: () => void
  onMakeDefault?: () => void
  onUpdateLocalAddresses?: (payload: LocalAddressesPayload) => void
}

const ACTIONS_BAR_BUTTON_CLASS =
  'flex items-center gap-1.5 px-3 py-[10.25px] text-[13px] text-postal-muted font-semibold disabled:opacity-60 disabled:cursor-not-allowed'

function formatStreetLine(
  street: string,
  number: string,
  complement?: string
): string {
  const complementText = complement ? ` — ${complement}` : ''

  return street && number
    ? `${street}, ${number}${complementText}`
    : street ?? ''
}

function formatCityState(city: string, state: string): string {
  return city && state ? `${city} – ${state}` : city ?? state
}

function formatNeighborhoodWithCity(
  neighborhood: string,
  cityState: string
): string {
  return [neighborhood, cityState].filter(Boolean).join(', ')
}

export function PostalCodeAddressCard({
  addressData,
  label = 'Casa',
  isDefault = false,
  isSelected = false,
  orderFormId,
  syncOrderFormFn,
  showActionsBar = false,
  onEdit,
  onSelect,
  onMakeDefault,
  onUpdateLocalAddresses,
}: PostalCodeAddressCardProps) {
  const {
    street = '',
    number = '',
    complement,
    neighborhood = '',
    city = '',
    state = '',
    postalCode = '',
    addressId = '',
  } = addressData ?? {}

  const {
    isDeletePending,
    setIsDeletePending,
    isDeleting,
    isMakingDefault,
    isActionsBusy,
    handleExcludeClick,
    handleMakeDefault,
    handleSelect,
  } = usePostalCodeAddressCardActions({
    addressId: addressId ?? '',
    orderFormId: orderFormId ?? '',
    syncOrderFormFn,
    onMakeDefault,
    onSelect,
    onUpdateLocalAddresses,
  })

  const streetLine = formatStreetLine(street, number, complement)
  const neighborhoodCityLine = formatNeighborhoodWithCity(
    neighborhood,
    formatCityState(city, state)
  )

  const resolvedReference = resolveAddressReferenceLabel(addressData)
  const displayTitle = resolvedReference || label

  const interactiveCardAttributes = {
    role: 'radio' as const,
    'aria-checked': isSelected,
    onClick: handleSelect,
  }

  return (
    <div>
      <button
        type="button"
        {...interactiveCardAttributes}
        data-address-card="true"
        className={classNames(
          'w-full text-left pl-4 pr-3 py-3.5 border flex gap-3',
          {
            'border-b-none cursor-pointer': showActionsBar,
            'bg-postal-subtle border-primaryBlue': isSelected,
            'border-postal-divider': !isSelected,
          }
        )}
      >
        <RadioCheckedIcon isSelected={isSelected} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <HomeIcon color="#101010" />
            <span className="text-font-size-3 font-semibold text-restructure-primary">
              {displayTitle}
            </span>
            {isDefault && (
              <span className="px-2 py-[3.5px] text-[10px] leading-3 uppercase font-bold text-restructure-action tracking-[0.5px] bg-postal-subtle">
                Padrão
              </span>
            )}
          </div>
          <p className="mt-1 text-[13px] text-restructure-primary leading-snug truncate font-normal">
            {streetLine}
          </p>
          <p className="text-[13px] text-postal-muted leading-snug truncate font-normal">
            {neighborhoodCityLine}
          </p>
          {postalCode && (
            <p className="text-[12px] text-postal-muted mt-0.5 font-normal">
              CEP {maskCEP(postalCode)}
            </p>
          )}
        </div>
        {!showActionsBar && (
          <div className="h-full flex self-center">
            <button
              className="flex items-center gap-1 text-postal-muted text-[13px] leading-normal py-2 px-[10px]"
              type="button"
              onClick={(event) => {
                event?.stopPropagation()
                onEdit?.()
              }}
            >
              <EditIcon />
              Editar
            </button>
          </div>
        )}
      </button>
      {showActionsBar && (
        <div
          aria-busy={isActionsBusy}
          className={classNames(
            'flex justify-between items-center w-full border p-2 bg-postal-page',
            {
              'border-primaryBlue border-t-postal-divider': isSelected,
              'border-postal-divider': !isSelected,
              'px-4 bg-postal-error': isDeletePending,
            }
          )}
        >
          {!isDeletePending && (
            <>
              <div className="flex gap-1">
                <button
                  className={ACTIONS_BAR_BUTTON_CLASS}
                  type="button"
                  onClick={onEdit}
                  disabled={isActionsBusy}
                >
                  <EditIcon />
                  Editar
                </button>
                {!isDefault && (
                  <button
                    onClick={handleMakeDefault}
                    className={ACTIONS_BAR_BUTTON_CLASS}
                    type="button"
                    disabled={isActionsBusy}
                  >
                    {isMakingDefault ? (
                      <>
                        <div
                          className="checkout-loader-button-dark"
                          style={{ margin: 0 }}
                        />
                        Tornando padrão...
                      </>
                    ) : (
                      <>
                        <DefaultIcon />
                        Tornar Padrão
                      </>
                    )}
                  </button>
                )}
              </div>
              <button
                onClick={() => {
                  setIsDeletePending(true)
                }}
                className={ACTIONS_BAR_BUTTON_CLASS}
                type="button"
                disabled={isActionsBusy}
              >
                <DeleteIcon />
                Excluir
              </button>
            </>
          )}
          {isDeletePending && (
            <>
              <span className="text-[13px] text-postal-error font-semibold leading-normal">
                Excluir esse endereço?
              </span>
              <div className="flex gap-4">
                <button
                  onClick={() => {
                    setIsDeletePending(false)
                  }}
                  className="text-[13px] text-postal-muted font-semibold leading-normal"
                  type="button"
                  disabled={isActionsBusy}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleExcludeClick}
                  className="text-[13px] text-postal-inverse font-semibold leading-normal px-3 py-[10.25px] bg-postal-error disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-1.5"
                  type="button"
                  disabled={isActionsBusy}
                >
                  {isDeleting ? (
                    <>
                      <div
                        className="checkout-loader-button"
                        style={{ margin: 0 }}
                      />
                      Excluindo...
                    </>
                  ) : (
                    'Excluir'
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}
